import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import { ChoiceListItemFetch, NoteEntryTemplateValue } from "ng/src/app/notes/shared/note-entry.model";
import { forkJoin, fromEvent, merge, Observable, of, Subject, Subscription } from "rxjs";
import { Nullable } from "ng/src/app/shared/model/app.model";
import { NoteService } from "ng/src/app/notes/shared/note.service";
import { NoteProjections } from "app/shared/model/request-parameter.model";
import { catchError, defaultIfEmpty, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { NoteTemplateValue } from "ng/src/app/templates/shared/notetemplatevalue.model";
import { DatePipe } from "@angular/common";
import { TemplateSegment, TemplateSegmentTypeId } from "ng/src/app/templates/shared/template.model";
import { TagValue } from "ng/src/app/tags/tag.model";
import { FileAttachment } from "ng/src/app/notes/shared/file-attachments/file-attachment.model";
import { Utils } from "ng/src/app/core/utils";
import { Note } from 'ng/src/app/notes/shared/note.model';
import { OverlayPanel } from "primeng/overlaypanel";
import { HttpErrorResponse } from "@angular/common/http";
import { TemplateService } from "ng/src/app/templates/shared/template.service";
import FastTemplateValue = NoteTemplateValue.FastTemplateValue;
import FastTemplateValues = Note.Projections.FastTemplateValues;
import { ChoiceListService } from "ng/src/app/choice-lists/shared/choice-list.service";
import { fromPromise } from "rxjs/internal-compatibility";
import { HasId } from "app/shared/model/properties.model";
import MetaData from "chart.js";
import ChartData from "chart.js";
import ChartDataSets from "chart.js";
import Chart from "chart.js";
import 'chartjs-adapter-moment';

@Component({
	selector: `lb-past-values[segment][note]`,
	providers: [DatePipe],
	templateUrl: './past-values.component.html',
	styleUrls: ['./past-values.component.scss'],
})
export class PastValuesComponent implements OnInit, OnDestroy {

	@Input() overlayNote = true;
	@Input() showContextValue = false;
	@Input() showPath = false;
	@Input() maxNoteHeight: string = '90vh';
	@Input() displayRefreshState = false;
	
	/**
	 * Consider the component to need new data when either the reference changes or the id changes.
	 * Default is 'id'.
	 */
	@Input() refreshOnUpdate: 'ref' | 'id' = 'id';
	
	@Input()
	get note() {
		return this._note;
	}
	set note(val) {
		const same = this.isSame(this._note, val);
		this._note = val;
		if (same) return;
		
		this.refresh();
	}
	
	@Input('segment')
	get templateSegment() {
		return this._segment;
	}
	set templateSegment(val) {
		const same = this.isSame(this._segment, val);
		this._segment = val;
		if (same) return;
		
		this.refresh();
	}
	
	@Input()
	get viewState() {
		return this._viewState;
	}
	set viewState(val) {
		this._viewState = val;
	}
	
	@Input()
	get displayTickLabels() {
		return this._displayTickLabels;
	}
	set displayTickLabels(val: boolean) {
		this._displayTickLabels = val;
		this.chartOptions = this.updateChartOptions();
	}
	
	@Output()
	viewStateChange = new EventEmitter<'VALUES' | 'NOTE'>();
	
	state: 'TABLE' | 'CHART' = 'TABLE';
	readonly stateOptions = [
		{
			label: 'Chart',
			value: 'CHART'
		},
		{
			label: 'Table',
			value: 'TABLE'
		}
	];
	
	get templateSegmentType() {
		return this.templateSegment.type.id;
	}
	
	get templateSegmentId() {
		return this.templateSegment.id;
	}
	
	get unitsDisplay() {
		return (!!this.templateSegment.units) ? ` ${this.templateSegment.units}` : '';
	}
	
	readonly SEGMENT_TYPES = TemplateSegmentTypeId;
	readonly onDestroy$ = new Subject();
	private readonly refreshSubject = new Subject();
	
	private _displayTickLabels = true;
	private _note!: Note;
	private _segment!: TemplateSegment;
	private _viewState: 'VALUES' | 'NOTE' = 'VALUES';
	
	noteToDetail?: Note;
	
	values: PastValueData[] = [];
	refreshing = false;
	refreshDate: Nullable<Date> = null;
	readonly linkListeners: {[noteId: number]: Subscription} = {};
	private refreshPending = false;
	private noteStateChangeSubscription?: Subscription;
	
	chartNoteDetail: {href: string, note: FastTemplateValues | null, date: Date} = {
		href: '',
		note: null,
		date: new Date()
	}
	chartLinkListener?: Subscription;
	
	isChartable = false;
	chartType: 'line' | 'bar' = 'line';
	chartData: ChartData.ChartData = {
		labels: [],
		datasets: [],
	}
	
	chartOptions: Chart.ChartOptions<'line'> = this.updateChartOptions();
	
	constructor(
		private noteService: NoteService,
		private templateService: TemplateService,
		private choiceListService: ChoiceListService,
		private datePipe: DatePipe
	) {}
	
	typeOf(val: any) {
		return typeof val;
	}
	
	ngOnInit(): void {
		this.refreshSubject.pipe(
			tap(() => {
				this.refreshing = true;
				this.refreshPending = false;
				this.noteStateChangeSubscription?.unsubscribe();
				this.isChartable = this.templateSegmentType === this.SEGMENT_TYPES.NUMERIC_INPUT;
				this.state = (this.isChartable) ? 'CHART' : 'TABLE';
			}),
			switchMap(() => this.noteService.getPastValueNotes(this.note, NoteProjections.fastTemplateValues)),
			switchMap(values => {
				this.noteStateChangeSubscription = merge(...values.map(v => this.noteService.register(v.id)))
					.pipe(
						takeUntil(this.onDestroy$)
					)
					.subscribe(() => this.refresh());
				
				return ((!this.showContextValue)
					? of(values)
					: this.noteService.getLogNote(this.note.id, NoteProjections.fastTemplateValues)
						.pipe(map(result => {
							return [result, ...values];
						})))
					.pipe(
						switchMap(notes => {
								const itemFetch = new ChoiceListItemFetch();
								const obs = notes.map(note => this.createPastValueData(note, itemFetch));
								return fromPromise(itemFetch.execute(this.choiceListService)).pipe(
									switchMap(() => {
										return forkJoin(obs);
									})
								)
						}),
						defaultIfEmpty([] as PastValueData[])
					);
			}),
			catchError((err: Error | HttpErrorResponse) => {
				console.error(`${err.name} ${err.message}`);
				return of([] as PastValueData[]);
			}),
			tap(() => {
				this.refreshing = false;
				this.refreshDate = new Date();
			})
		)
			.subscribe((values) => {
				this.values = values;
				if (!this.isChartable) return;
				this.chartData = this.getChartData(values as PastValueData<string | number>[]);
			});
		
		if (this.refreshPending)
			this.refresh();
	}
	
	ngOnDestroy(): void {
		this.onDestroy$.next();
	}
	
	
	displayNote(type: 'TABLE', event: MouseEvent, panel: OverlayPanel, note: Note): void;
	displayNote(type: 'CHART', event: DataClickEvent, panel: OverlayPanel, note: null): void;
	displayNote(type: 'TABLE' | 'CHART', event: MouseEvent | DataClickEvent, panel: OverlayPanel, note: Note | null): void {
		const getChartPastValue = () => {
			const cEvent = event as DataClickEvent;
			return [...this.values].reverse()[cEvent.element.index];
		}
		const detailNote = (note: Note) => {
			this.noteToDetail = note;
			this.viewState = 'NOTE';
		}
		if (type === 'TABLE') {
			const cEvent = event as MouseEvent;
			if (cEvent.metaKey || cEvent.ctrlKey) {
				return
			}
			cEvent.preventDefault();
			if (!this.overlayNote) {
				detailNote(note!);
				return;
			}
			panel.show(event);
		}
		else if (type === 'CHART') {
			const cEvent = event as DataClickEvent;
			const pastValue = getChartPastValue();
			
			if ((!pastValue || pastValue.isContextValue) && cEvent.originalEvent.target) return;
			
			if (!this.overlayNote) {
				detailNote(pastValue.note);
				return;
			}
			this.chartNoteDetail.date = pastValue.date;
			this.chartNoteDetail.href = pastValue.detailHref;
			this.chartNoteDetail.note = pastValue.note;
			panel.show(cEvent.originalEvent);
		}
	}
	
	refresh() {
		this.refreshSubject.next();
		this.refreshPending = true;
	}
	
	setOutOfRangeAttribute(el: HTMLElement, outOfRange: boolean) {
		el.toggleAttribute('data-oor-cell', outOfRange);
	}
	
	updateLinkListener(noteId: number, element?: HTMLElement) {
		this.linkListeners[noteId]?.unsubscribe();
		delete this.linkListeners[noteId];
		if (!element) return;
		const sub = this.createLinkListenerSubscription(element);
		if (sub) this.linkListeners[noteId] = sub;
	}
	
	updateChartLinkListener(element?: HTMLElement) {
		setTimeout(() => {
			this.chartLinkListener?.unsubscribe();
			delete this.chartLinkListener;
			if (!element) return;
			this.chartLinkListener = this.createLinkListenerSubscription(element);
		})
	}
	
	attemptScrollToSegment(element: HTMLElement) {
		setTimeout(() => {
			const elementToScroll = element.querySelector(`#segment-row-${this.templateSegmentId}`) as HTMLElement;
			if (!elementToScroll) return;
			try {
				elementToScroll.scrollIntoView({ block: 'center' });
			}
			catch (e) {
				elementToScroll.scrollIntoView();
			}
			elementToScroll.classList.toggle('highlight', true);
		})
	}
	
	private isSame(val1?: HasId, val2?: HasId) {
		if (!val1 || !val2) return false;
		return this.refreshOnUpdate === 'id' ? val1.id === val2.id : val1 === val2;
	}
	
	private updateChartOptions(): Chart.ChartOptions<'line'> {
		return {
			plugins: {
				tooltip: {
					filter: (item) => (item.datasetIndex ?? 0) === 0
				},
			},
			layout: {
				padding: {
					right: 6
				}
			},
			scales: {
				x: {
					type: 'time',
					time: {
						displayFormats: {
							year: 'YYYY',
							month: 'MM/YYYY',
							day: 'MM/DD/YYYY',
							hour: 'HH:mm',
							second: 'HH:mm:ss'
						}
					}
				},
				y: {}
			}
		};
	}
	
	private createLinkListenerSubscription(element: HTMLElement): Subscription | undefined {
		const nodeList = element.querySelectorAll('a');
		if (nodeList.length <= 0) return;
		return fromEvent(nodeList, 'click')
			.pipe(
				takeUntil(this.onDestroy$)
			)
			.subscribe((event) => {
				const target: HTMLAnchorElement | null = event.currentTarget as HTMLAnchorElement;
				if (!target || target.onclick) return;
				event.preventDefault();
				window.open(target.href, '_blank');
			})
	}
	
	private createPastValueData(note: FastTemplateValues, itemFetch: ChoiceListItemFetch): Observable<PastValueData> {
		const ntv = note.templateValues.find(ntv => ntv.templateSegmentId === this.templateSegmentId);
		let value: PastValueDataType;
		if (ntv)
			value = this.getValue(ntv);
		else
			value = 'No Value';
		
		const valueData: PastValueData = {
			key: note.id,
			value: value,
			date: new Date(note.noteTime),
			note: note,
			detailHref: `/#/note-detail?log=${note.logId}&noteId=${note.id}`,
			attachments: ntv?.commentAttachments ?? [],
			comment: ntv?.comment ?? '',
			ntv: ntv ?? null,
			outOfRange: false,
			isContextValue: note.id === this.note.id,
			mustRender: !!(ntv && ntv.richTextValue)
		}
		if (!ntv) return of(valueData);
		
		return this.isOutOfRange(ntv, itemFetch)
			.pipe(map(outOfRange => {
				valueData.outOfRange = !!outOfRange;
				return valueData;
			}))
	}
	
	private isOutOfRange(ntv: FastTemplateValue, itemFetch: ChoiceListItemFetch) {
		return this.templateService.isValueAbnormalOrOutOfRange(this.templateSegment, ntv, itemFetch);
	}
	
	private getValue(value: FastTemplateValue): PastValueDataType {
		let result = NoteEntryTemplateValue.getEditorValueFromValue(value, this.templateSegment);
		
		if (result == null) {
			return 'No Value';
		}
		else if (typeof result === 'boolean') {
			return result ? 'Checked' : 'Unchecked'
		}
		else if (typeof result === 'string' || typeof result === 'number') {
			return result;
		}
		else if (result instanceof Date) {
			let format = '';
			let includeTime = this.templateSegment.includeTime;
			let includeDate = this.templateSegment.includeDate || !includeTime;
			
			if (includeDate) {
				format = 'MM/dd/yyyy';
			}
			if (includeTime) {
				format = (format) ? `${format} HH:mm` : 'HH:mm';
			}
			return this.datePipe.transform(result, format) ?? 'Invalid Value';
		}
		else if (Array.isArray(result) && result.length > 0) {
			if (result.length > 0) {
				if (('viewString' in result[0])) {
					return (result as TagValue[])
						.map(val => val.viewString)
						.sort(Utils.createSortFunction(v => v))
						.join(', ');
				}
				return (result as FileAttachment[]).sort(Utils.createSortFunction(v => new Date(v.created)));
			}
			return 'No Values';
		}
		else if ('lastName' in result) {
			return `${result.firstName} ${result.lastName}`;
		}
		return 'No Value';
	}
	
	private getChartData(values: PastValueData<string | number>[]): ChartData.ChartData {
		values = [...values].reverse();
		
		const getColor = (value: PastValueData) =>
			value.isContextValue ? 'rgba(59, 173, 53, 0.8)' : value.outOfRange ? 'rgba(216, 15, 15, 0.8)' : 'rgba(8, 139, 255, 0.8)';
		
		const data: ChartData.ChartData = {
			labels: values.map(v => this.datePipe.transform(v.date, 'MM/dd/yyyy HH:mm') ?? ''),
			datasets: [
				{
					lineTension: 0.1,
					label: `${this.templateSegment.name}${this.unitsDisplay}`,
					showLine: true,
					data: values.map(v => {
						return {
							x: v.date,
							y: typeof v.value === 'number' ? v.value : NaN
						} as any
					}),
					backgroundColor: 'rgba(8, 139, 255, 0.8)',
					borderColor: 'rgba(8, 139, 255, 0.8)',
					pointBorderColor: values.map(getColor),
					pointBackgroundColor: values.map(getColor),
					pointStyle: values.map(v => !!(v.comment || v.attachments.length) ? 'rectRot' : 'circle'),
					pointBorderWidth: 3,
					fill: false
				} as ChartDataSets.ChartDataset
			]
		}
		if (typeof this.templateSegment.maxValue === 'number') {
			data.datasets?.push({
				type: 'line',
				label: 'Max Value',
				data: values.map(v => {
					return {
						x: v.date,
						y: this.templateSegment.maxValue!
					} as any
				}),
				fill: false,
				showLine: true,
				backgroundColor: 'rgba(216, 15, 15, 0.8)',
				borderColor: 'rgba(216, 15, 15, 0.8)',
				pointRadius: 0,
				hoverRadius: 0
			} as ChartDataSets.ChartDataset)
		}
		if (typeof this.templateSegment.minValue === 'number') {
			data.datasets?.push({
				type: 'line',
				label: 'Min Value',
				data: values.map(v => {
					return {
						x: v.date,
						y: this.templateSegment.minValue!
					} as any
				}),
				fill: false,
				backgroundColor: 'rgba(255, 185, 8, 0.8)',
				borderColor: 'rgba(255, 185, 8, 0.8)',
				pointRadius: 0,
				hoverRadius: 0
			} as ChartDataSets.ChartDataset)
		}
		
		return data;
	}
}

type DataClickEvent = {
	dataset: MetaData.ChartMeta[],
	element: MetaData.ChartMeta,
	originalEvent: PointerEvent
}
type PastValueDataType = string | number | Array<FileAttachment>;
type PastValueData<T extends PastValueDataType = PastValueDataType> = {
	key: number,
	value: T,
	date: Date,
	note: FastTemplateValues,
	ntv: Nullable<FastTemplateValue>,
	attachments: FileAttachment[],
	comment: string,
	detailHref: string,
	outOfRange: boolean,
	isContextValue: boolean,
	mustRender: boolean
}